import { useEffect, useRef } from 'react';

import { normaliseStepDataDE } from '../../components/helpers/stateNormalisation';
import { getLatestStepDE } from '../../helpers/getLatestStep/getLatestStepDE';
import { TGetStateResponseDE } from '../../lapiOverrideTypesDE';
import {
  useGetApplicationQuery,
  useGetContext,
  useGetProfile,
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../lendingApi/lendingApiQueries';
import logger from '../../utils/logger';
import { useContextStateKey } from '../useContextStateKey';
import { useSignupRouterDE } from '../useSignupRouter/useSignupRouterDE';

export const useAutoRoutingDE = () => {
  const stateKey = useContextStateKey();

  const hasRedirectedRef = useRef(false);

  const { isProfileErrored } = useGetProfile();
  const { context, contextStatus } = useGetContext();
  const { state, refetchState } = useGetStateQuery<TGetStateResponseDE>();

  const { application, applicationStatus, refetchApplication } =
    useGetApplicationQuery();

  const { normalisedState } = useGetSelectStateQuery((data) =>
    normaliseStepDataDE(data),
  );

  const { currentStep, navigateSignupDE, navigateToAccountPages } =
    useSignupRouterDE();

  // Stop future auto redirects if profile errors
  useEffect(() => {
    if (isProfileErrored) {
      hasRedirectedRef.current = true;
    }
  }, [isProfileErrored]);

  // Refetches application if restored by bfcache
  useEffect(() => {
    const handleRefetch = async (event: PageTransitionEvent) => {
      logger.info('Page show event triggered', { event: event });
      if (event.persisted) {
        logger.info('Cache has been restored');
        logger.debug('Application status before refetch', {
          status: application.status,
          closed: application.is_closed,
        });
        logger.debug('Redirect ref value', { value: hasRedirectedRef.current });

        await refetchState();

        logger.debug('Application status after state refetch', {
          status: application.status,
          closed: application.is_closed,
          context: context.user_signup_finished,
        });

        hasRedirectedRef.current = false;

        logger.info('State has been refetched');

        const fetchedApplication = (await refetchApplication()).data;

        logger.debug('Application status after refetch', {
          status: fetchedApplication?.data.status,
          closed: fetchedApplication?.data.is_closed,
        });
      }
    };

    window.addEventListener('pageshow', handleRefetch);

    return () => window.removeEventListener('pageshow', handleRefetch);
  }, [refetchApplication, application, refetchState, context]);

  useEffect(() => {
    logger.debug('UseEffect - Application', {
      status: applicationStatus,
      ref: hasRedirectedRef.current,
    });

    if (
      normalisedState &&
      typeof stateKey === 'string' &&
      applicationStatus === 'success' &&
      !hasRedirectedRef.current
    ) {
      if (application?.status === 'deferred') {
        void navigateSignupDE('neu', stateKey);
      }

      if (
        application?.status === 'offered' ||
        application?.is_closed ||
        context.user_signup_finished
      ) {
        logger.info('Navigating to Account pages');
        return void navigateToAccountPages(stateKey);
      }

      hasRedirectedRef.current = true;

      const activeStep = normalisedState.ui?.activePartnerStep;
      const newStep = getLatestStepDE(state, application, activeStep);

      if (newStep !== currentStep) {
        void navigateSignupDE(newStep, stateKey, state);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application, applicationStatus, contextStatus, context, normalisedState]);
};
